/**
 * AchievementsPanel — own-profile right-rail card showing achievement badges.
 *
 * Maps tone → icon: gold → 'award', blue → 'shield', violet → 'star', fallback → 'sparkles'.
 */
import styled from 'styled-components';
import { Panel, SectionLabel } from '@/ui';
import { Icon } from '@/icons';
import type { IconName } from '@/icons';
import type { Achievement, User } from '@/core/models';

/* ── Tone → icon map ────────────────────────────────────────────────────────── */

const TONE_ICON: Record<string, IconName> = {
  gold: 'award',
  blue: 'shield',
  violet: 'star',
};

function iconFor(tone: string): IconName {
  return TONE_ICON[tone] ?? 'sparkles';
}

/* ── Styled helpers ─────────────────────────────────────────────────────────── */

const BadgeList = styled.ul`
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const BadgeChip = styled.li`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 6px 10px;
  border-radius: ${({ theme }) => theme.radius.sm};
  background: ${({ theme }) => theme.color.glassBg};
  border: 1px solid ${({ theme }) => theme.color.glassBorder};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: 600;
  color: ${({ theme }) => theme.color.textPrimary};
  width: fit-content;
`;

const ToneIcon = styled.span<{ $tone: string }>`
  display: flex;
  align-items: center;
  color: ${({ $tone, theme }) =>
    $tone === 'gold'
      ? theme.color.accentAmber
      : $tone === 'blue'
        ? theme.color.accentCyan
        : $tone === 'violet'
          ? theme.color.brandFrom
          : theme.color.textMuted};
`;

const EmptyState = styled.p`
  margin: 0;
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.color.textMuted};
`;

/* ── Component ──────────────────────────────────────────────────────────────── */

export interface AchievementsPanelProps {
  user: User;
}

function AchievementChipItem({ a }: { a: Achievement }) {
  return (
    <BadgeChip>
      <ToneIcon $tone={a.tone}>
        <Icon name={iconFor(a.tone)} size={14} />
      </ToneIcon>
      {a.name}
    </BadgeChip>
  );
}

export function AchievementsPanel({ user }: AchievementsPanelProps) {
  const achievements = user.achievements ?? [];

  return (
    <Panel>
      <SectionLabel as="h2">Achievements</SectionLabel>

      {achievements.length === 0 ? (
        <EmptyState>No achievements yet.</EmptyState>
      ) : (
        <BadgeList>
          {achievements.map((a) => (
            <AchievementChipItem key={a.id} a={a} />
          ))}
        </BadgeList>
      )}
    </Panel>
  );
}
